import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Calendar, Check, ChevronDown, ExternalLink, FileText, Info, Link2, Lock, Loader2, MessageSquare, Phone, Plus, AlertTriangle, Clock, Zap, RotateCcw } from "lucide-react";
import { Twemoji } from 'react-emoji-render';
import axios from 'axios';
import { api, useAuth } from "@/contexts/AuthContext";
import {
  CALENDAR_CAMPAIGN_CONTEXT,
  isCalendarOAuthMessageContext,
} from "@/lib/calendarCampaignFlow";
import { changeCalendarAccount, startCalendarCampaignOAuth } from "@/lib/calendarOAuth";
import { useDefaultCountryCode } from "@/hooks/useDefaultCountryCode";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "./ui/card";
import { ColumnSelect } from "./ui/select";
import TemplateCreatorModal from "./TemplateCreatorModal";
import StepTemplate from "./renewals/StepTemplate";
import StepTemplateMessage from "./renewals/StepTemplateMessage";
import { calendarService, CalendarCampaign, CalendarCampaignCreate, CalendarEvent, CalendarListEntry } from "../services/calendarService";
import { AppointmentRemindersConfig } from "../features/dynamic-campaigns/components/AppointmentRemindersConfig";
import {
  CALENDAR_STANDARD_FIELD_OPTIONS,
  calendarFieldLabelToKey,
  findInvalidCalendarMappingLabels,
  isAllowedCalendarFieldKey,
} from "@/lib/calendarFieldOptions";
import {
  buildCalendarSampleRowFromEvent,
  extractDescriptionVars,
  pickNewestCalendarEvent,
} from "@/lib/calendarPreviewUtils";
import {
  buildCalendarColumnMapping,
  buildMetaTemplateRequest,
  extractNumericPlaceholderKeys,
  normalizeMetaTemplateName,
  validateMetaTemplateDraft,
} from "@/lib/metaTemplateUtils";
import {
  normalizeMetaTemplate,
  normalizeLocalTemplate,
} from "@/lib/templatePreview";

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// ─── Phone extraction (mirrors backend logic) ───────────────────────────────
// NOTE: COUNTRY_CODE and CC_DIGITS are now dynamic — see component body below
function extractPhone(description: string, ccDigits: string): string | null {
  if (!description) return null;
  // Match: optional +, country code digits, optional separator, 7-12 local digits
  const re = new RegExp(
    `(?<!\\d)\\+?${ccDigits}[\\s\\-.]?(\\d[\\s\\-.]?){7,12}(?!\\d)`
  );
  const m = description.match(re);
  if (!m) return null;
  const raw = m[0].replace(/\D/g, "");
  if (!raw.startsWith(ccDigits)) return null;
  return raw;
}

function hasPhone(event: CalendarEvent, ccDigits: string): boolean {
  return !!extractPhone(event.description || "", ccDigits);
}

function hasDescriptionVars(event: CalendarEvent): boolean {
  return Object.keys(extractDescriptionVars(event.description || "")).length > 0;
}

// Extract unique {{variable}} names from a template body (preserving order, no duplicates)
function extractTemplateVars(text: string): string[] {
  const matches = text.match(/\{\{([^}]+)\}\}/g) || [];
  const seen = new Set<string>();
  return matches
    .map(m => m.replace(/\{\{|\}\}/g, "").trim())
    .filter(v => { if (seen.has(v)) return false; seen.add(v); return true; });
}

// ─── Types ───────────────────────────────────────────────────────────────────
interface Props {
  calendars: CalendarListEntry[];
  campaigns: CalendarCampaign[];
  onCreated: (campaign: CalendarCampaign) => void;
  onCancel: () => void;
  onStepChange?: (step: number) => void;
  initialCampaignName?: string;
}

const STEPS = ["Campaña", "Recordatorio", "Plantilla", "Mensaje", "Confirmar", "Finalizado"];

// ─── Component ───────────────────────────────────────────────────────────────
const CalendarCampaignBuilder: React.FC<Props> = ({ calendars, campaigns, onCreated, onCancel, onStepChange, initialCampaignName }) => {
  const { token } = useAuth();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  // ✅ Dynamic country code from WhatsApp connection
  const { defaultCountryCode } = useDefaultCountryCode();
  const [countryCode, setCountryCode] = useState("+57");
  useEffect(() => { setCountryCode(defaultCountryCode); }, [defaultCountryCode]);
  const ccDigits = countryCode.replace("+", "");

  // Step 1
  const [selectedCalendarId, setSelectedCalendarId] = useState<string | null>(null);

  // Step 0 — events (loaded on calendar selection)
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  // Step 1 — reminder config
  const [useDefault, setUseDefault] = useState(true);
  const [reminderDays, setReminderDays] = useState<number[]>([]);
  const [reminderHours, setReminderHours] = useState<number[]>([]);

  // Step 4 — template
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState<string | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Template creation (auto name/type/language set in StepTemplate)
  const [templateType, setTemplateType] = useState("Utilidad: Alertas y actualizaciones");
  const [templateLanguage, setTemplateLanguage] = useState("Español");
  const [templateNameInput, setTemplateNameInput] = useState("");
  const [showTutorialModal, setShowTutorialModal] = useState(false);

  // Campaign name
  const [campaignName, setCampaignName] = useState(initialCampaignName || "");

  // Message Editor States (for new templates)
  const [messageTemplate, setMessageTemplate] = useState("");
  const [templateHeaderText, setTemplateHeaderText] = useState("");
  const [templateFooterText, setTemplateFooterText] = useState("");
  const [templateHeaderType, setTemplateHeaderType] = useState<"TEXT" | "IMAGE">("TEXT");
  const [templateHeaderImageHandle, setTemplateHeaderImageHandle] = useState("");
  const [templateHeaderImageUrl, setTemplateHeaderImageUrl] = useState("");
  const [templateHeaderImagePreview, setTemplateHeaderImagePreview] = useState("");
  const [templateButtons, setTemplateButtons] = useState<any[]>([]);

  const [templateHeaderEnabled, setTemplateHeaderEnabled] = useState(false);
  const [templateFooterEnabled, setTemplateFooterEnabled] = useState(false);
  const [templateButtonsEnabled, setTemplateButtonsEnabled] = useState(false);
  const [createdCampaign, setCreatedCampaign] = useState<any>(null);
  const [isPendingApproval, setIsPendingApproval] = useState(false);

  useEffect(() => {
    if (onStepChange) onStepChange(step);
  }, [step, onStepChange]);

  // Variable mapping for template parameters
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});

  // Reset mapping when the selected template changes — user maps manually from the list
  useEffect(() => {
    setColumnMapping({});
  }, [selectedTemplateId]);

  // ── Derived ────────────────────────────────────────────────────────────────
  // Local calendar list (can be updated by inline picker without leaving page)
  const [localCalendars, setLocalCalendars] = useState(calendars);
  const [changingAccount, setChangingAccount] = useState(false);
  // Keep in sync if prop changes on first mount
  useEffect(() => { setLocalCalendars(calendars); }, [calendars]);

  const reloadLocalCalendars = useCallback(async () => {
    try {
      const updated = await calendarService.listCalendars();
      setLocalCalendars(updated || []);
    } catch {
      setLocalCalendars([]);
    }
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'google_oauth_success' && isCalendarOAuthMessageContext(event.data?.context)) {
        toast.success('Google Calendar conectado correctamente');
        void reloadLocalCalendars();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [reloadLocalCalendars]);

  const selectedCalendar = localCalendars.find((c) => c.id === selectedCalendarId);

  // Estado de activación (spinner por calendario) y paginado del grid
  const [activatingCal, setActivatingCal] = useState<string | null>(null);
  const [calPage, setCalPage] = useState(0);
  const CAL_PER_PAGE = 4; // 2 columnas × 2 filas (estilo Google Sheets)

  /** Activa un calendario inactivo vía API y lo selecciona directamente */
  async function activateAndSelect(calId: string) {
    if (activatingCal) return;
    setActivatingCal(calId);
    try {
      await axios.post(`${BACKEND}/api/calendar/activate`, { calendar_id: calId, active: true }, { withCredentials: true });
      const updated = await calendarService.listCalendars();
      setLocalCalendars(updated || []);
      setSelectedCalendarId(calId);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Error al activar el calendario');
    } finally {
      setActivatingCal(null);
    }
  }

  // Estado del formulario de creación inline de calendario
  const [showCreateCalForm, setShowCreateCalForm] = useState(false);
  const [newCalName, setNewCalName] = useState('');
  const [newCalDesc, setNewCalDesc] = useState('');
  const [creatingCal, setCreatingCal] = useState(false);

  /** Crea un nuevo Google Calendar y lo activa+selecciona directamente */
  async function createNewCalendar() {
    if (!newCalName.trim()) { toast.error('Escribe un nombre para el calendario'); return; }
    setCreatingCal(true);
    try {
      // POST /api/calendar — axios directo con withCredentials
      const res = await axios.post(
        `${BACKEND}/api/calendar`,
        { summary: newCalName.trim(), description: newCalDesc.trim() || undefined },
        { withCredentials: true }
      );
      const newCal = res.data?.calendar;
      if (!newCal?.id) throw new Error('No se recibió el ID del calendario creado');
      // Activarlo automáticamente
      await axios.post(`${BACKEND}/api/calendar/activate`, { calendar_id: newCal.id, active: true }, { withCredentials: true });
      // Refrescar lista y seleccionarlo
      const updated = await calendarService.listCalendars();
      setLocalCalendars(updated || []);
      setSelectedCalendarId(newCal.id);
      setShowCreateCalForm(false);
      setNewCalName('');
      setNewCalDesc('');
      toast.success(`Calendario "${newCal.summary}" creado y seleccionado`);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Error al crear el calendario');
    } finally {
      setCreatingCal(false);
    }
  }

  function calendarHasActiveCampaign(calId: string): boolean {
    return campaigns.some(
      (c) =>
        c.calendar_id === calId &&
        (c.status === "active" ||
          c.status === "pending_template_approval" ||
          c.status === "paused")
    );
  }
  const qualifyingEvents = events.filter(e => hasPhone(e, ccDigits));
  const nonQualifyingCount = events.length - qualifyingEvents.length;

  const selectedTemplate = templates.find(
    (t) => String(t.id ?? t._id) === String(selectedTemplateId)
  );
  const templateIsPending = selectedTemplate?.status === "PENDING";

  const templateBody: string =
    selectedTemplate?.template ||
    selectedTemplate?.body ||
    selectedTemplate?.components?.find((c: any) => c.type === "BODY")?.text ||
    "";

  // ── Variable-related derived values ────────────────────────────────────────
  const templateVars = extractTemplateVars(templateBody);
  const hasTemplateVars = templateVars.length > 0;

  // All unique description keys found across ALL events (so users can build templates even if some events lack phone numbers)
  const eventDescVarKeys: string[] = Array.from(
    new Set(events.flatMap(e => Object.keys(extractDescriptionVars(e.description || ""))))
  );
  const eventsWithVars = events.filter(hasDescriptionVars);
  const hasEventsWithVars = eventsWithVars.length > 0;

  // Options for the column mapping dropdowns: standard fields + description keys from events
  const mappingOptionLabels = [
    ...CALENDAR_STANDARD_FIELD_OPTIONS.map((f) => f.label),
    ...eventDescVarKeys,
  ];

  const sampleEvent =
    pickNewestCalendarEvent(qualifyingEvents) ??
    pickNewestCalendarEvent(events);
  const sampleRowMock = buildCalendarSampleRowFromEvent(sampleEvent);

  // ── Load events whenever a calendar is selected ───────────────────────────
  useEffect(() => {
    if (!selectedCalendarId) { setEvents([]); return; }
    setLoadingEvents(true);
    calendarService
      .getEvents(selectedCalendarId)
      .then((evts) => setEvents(evts || []))
      .catch(() => setEvents([]))
      .finally(() => setLoadingEvents(false));
  }, [selectedCalendarId]);

  const reloadProcessedTemplates = useCallback(async () => {
    try {
      const [metaRes, localRes] = await Promise.allSettled([
        api.get("/whatsapp/templates/meta/list"),
        api.get("/templates"),
      ]);
      const localList =
        localRes.status === "fulfilled" ? (localRes.value.data || []) : [];

      if (metaRes.status === "fulfilled" && metaRes.value.data?.templates?.length) {
        const processed = metaRes.value.data.templates
          .filter((t: any) => t.status === "APPROVED")
          .map((t: any) => normalizeMetaTemplate(t, localList));
        if (processed.length > 0) {
          setTemplates(processed);
          return;
        }
      }
      if (localList.length > 0) {
        setTemplates(localList.map((t: any) => normalizeLocalTemplate(t)));
        return;
      }
      setTemplates([]);
    } catch {
      /* keep current list */
    }
  }, []);

  // ── Load templates when reaching step 2 (same pattern as useRenewalCreate) ─
  useEffect(() => {
    if (step !== 2) return;
    setLoadingTemplates(true);
    reloadProcessedTemplates().finally(() => setLoadingTemplates(false));
  }, [step, reloadProcessedTemplates]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  function getReminderTime(eventStart: string): string {
    const dt = new Date(eventStart);
    if (isNaN(dt.getTime())) return "—";
    const effectiveDays = useDefault ? [1] : reminderDays;
    const effectiveHours = useDefault ? [1] : reminderHours;
    const timestamps: number[] = [
      ...effectiveDays.map((d) => dt.getTime() - d * 86400000),
      ...effectiveHours.map((h) => dt.getTime() - h * 3600000),
    ];
    if (timestamps.length === 0) return "—";
    return new Date(Math.min(...timestamps)).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" });
  }

  function getEventStart(event: CalendarEvent): string {
    return event.start.dateTime || event.start.date || "";
  }

  // Keep confirm UI name in sync when selecting an existing template via StepTemplate
  useEffect(() => {
    if (!selectedTemplateId) {
      setSelectedTemplateName(null);
      return;
    }
    const t = templates.find((tpl) => String(tpl.id ?? tpl._id) === String(selectedTemplateId));
    if (!t) return;
    const display =
      t.displayName ??
      t.name?.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()) ??
      `Plantilla ${t.id ?? t._id}`;
    setSelectedTemplateName(display);
  }, [selectedTemplateId, templates]);

  // ── Navigation ────────────────────────────────────────────────────────────
  function canNext(): boolean {
    if (step === 0) {
      const calOk = !!selectedCalendarId && !calendarHasActiveCampaign(selectedCalendarId) && campaignName.trim().length > 0;
      const reminderOk = useDefault || (reminderDays.length > 0 || reminderHours.length > 0);
      return calOk && reminderOk;
    }
    // step 1 is skipped
    if (step === 2) {
      // Create flow auto-fills name/type/language in StepTemplate before advancing.
      return !!selectedTemplateId || templateNameInput.trim().length > 0;
    }
    return true;
  }

  function nextStep() {
    if (!canNext()) return;
    // We are no longer using the popup modal, we use StepTemplateMessage instead.
    // So next step just advances. StepTemplate has its own canNext checks.
    setStep((s) => {
      if (s === 0) {
        setLoadingTemplates(true); // avoid empty-create flash before fetch starts
        return 2;
      }
      return s + 1;
    });
  }

  function prevStep() {
    // Skip step 1 when going back from step 2
    setStep((s) => s === 2 ? 0 : Math.max(0, s - 1));
  }

  // ── Submit ─────────────────────────────────────────────────────────────────
  async function handleSubmit() {
    if (!selectedCalendarId) return;

    const isNewTemplateFlow = !selectedTemplateId;
    let finalTemplateId = selectedTemplateId;
    let finalTemplateName = selectedTemplateName || undefined;
    let finalMessageTemplate = messageTemplate;
    let backendMapping: Record<string, string> = {};

    setSubmitting(true);
    try {
      if (isNewTemplateFlow) {
        const mappingResult = buildCalendarColumnMapping(
          {
            bodyText: messageTemplate,
            headerEnabled: templateHeaderEnabled,
            headerType: templateHeaderType,
            headerText: templateHeaderText,
            buttonsEnabled: templateButtonsEnabled,
            buttons: templateButtons,
          },
          columnMapping,
        );

        if (mappingResult.missingKeys.length > 0) {
          toast.error(
            `No se pudo resolver el mapeo de variables del mensaje. Revisa que uses variables del evento (por ejemplo {{name}}, {{hora}}) en tu plantilla.`,
          );
          setSubmitting(false);
          return;
        }

        finalMessageTemplate = mappingResult.messageTemplate;
        backendMapping = Object.fromEntries(
          Object.entries(mappingResult.columnMapping).map(([key, fieldKey]) => [
            key,
            calendarFieldLabelToKey(fieldKey),
          ]),
        );
      } else {
        const bodyText =
          selectedTemplate?.template ||
          selectedTemplate?.body ||
          selectedTemplate?.components?.find((c: any) => c.type === "BODY")?.text ||
          messageTemplate ||
          " ";
        finalMessageTemplate = bodyText;

        backendMapping = Object.fromEntries(
          Object.entries(columnMapping)
            .filter(([, label]) => label?.trim())
            .map(([key, label]) => [key, calendarFieldLabelToKey(label)]),
        );

        const requiredKeys = Array.from(
          new Set([
            ...extractNumericPlaceholderKeys(finalMessageTemplate),
            ...(selectedTemplate?.header_text
              ? extractNumericPlaceholderKeys(selectedTemplate.header_text)
              : []),
          ]),
        );
        const missingKeys = requiredKeys.filter((key) => !backendMapping[key]?.trim());
        if (missingKeys.length > 0) {
          toast.error(
            `Debes conectar todos los datos del calendario antes de crear la campaña. Faltan: ${missingKeys.map((k) => `{{${k}}}`).join(", ")}`,
          );
          setSubmitting(false);
          return;
        }
      }

      const invalidMappingLabels = findInvalidCalendarMappingLabels(
        Object.values(columnMapping).filter((label) => Boolean(label?.trim())),
        mappingOptionLabels,
      );
      if (invalidMappingLabels.length > 0) {
        toast.error(
          `Variables no encontradas en el calendario seleccionado: ${invalidMappingLabels.join(", ")}. ` +
            "Usa solo Fecha, Hora, Ubicación o campos detectados en las descripciones de tus eventos.",
        );
        setSubmitting(false);
        return;
      }

      const invalidFieldKeys = Object.values(backendMapping).filter(
        (fieldKey) => !isAllowedCalendarFieldKey(fieldKey, eventDescVarKeys),
      );
      if (invalidFieldKeys.length > 0) {
        toast.error(
          `Campos no reconocidos del calendario: ${invalidFieldKeys.join(", ")}.`,
        );
        setSubmitting(false);
        return;
      }

      // 1. Create Template in Meta if creating a new one
      if (isNewTemplateFlow && templateNameInput && messageTemplate) {
        toast.info("Creando plantilla en Meta...");
        const metaTemplateName = normalizeMetaTemplateName(templateNameInput);

        if (!metaTemplateName || metaTemplateName.length === 0) {
          toast.error("El nombre de la plantilla debe contener letras o números (no solo caracteres especiales).");
          setSubmitting(false);
          return;
        }

        const draftValidation = validateMetaTemplateDraft({
          bodyText: messageTemplate,
          headerEnabled: templateHeaderEnabled,
          headerType: templateHeaderType,
          headerText: templateHeaderText,
          headerImageHandle: templateHeaderImageHandle,
          footerEnabled: templateFooterEnabled,
          footerText: templateFooterText,
          buttonsEnabled: templateButtonsEnabled,
          buttons: templateButtons,
        });

        if (!draftValidation.valid) {
          toast.error(draftValidation.message);
          setSubmitting(false);
          return;
        }

        const metaTemplateRequest = buildMetaTemplateRequest({
          name: templateNameInput,
          language: templateLanguage,
          category: templateType,
          bodyText: messageTemplate,
          headerEnabled: templateHeaderEnabled,
          headerType: templateHeaderType,
          headerText: templateHeaderText,
          headerImageHandle: templateHeaderImageHandle,
          headerImageUrl: templateHeaderImageUrl,
          footerEnabled: templateFooterEnabled,
          footerText: templateFooterText,
          buttonsEnabled: templateButtonsEnabled,
          buttons: templateButtons,
        });

        const templateResponse = await api.post('/whatsapp/templates/create', metaTemplateRequest);
        if (templateResponse.data.success) {
          finalTemplateId = templateResponse.data.template_id;
          finalTemplateName = metaTemplateName;
          toast.success(`Plantilla "${metaTemplateName}" creada (Estado: ${templateResponse.data.status})`);
        } else {
          throw new Error(templateResponse.data.error || "Error al crear plantilla");
        }
      }

      if (!finalTemplateId) {
        throw new Error("No se pudo obtener la plantilla para la campaña");
      }

      const payload: CalendarCampaignCreate = {
        name: campaignName.trim(),
        calendar_id: selectedCalendarId,
        template_id: finalTemplateId,
        is_new_template: isNewTemplateFlow,
        message_template: finalMessageTemplate,
        calendar_config: {
          calendar_id: selectedCalendarId,
          timezone: "America/Bogota",
          reminder_days_before: useDefault ? [1] : reminderDays,
          reminder_hours_before: useDefault ? [1] : reminderHours,
        },
        column_mapping: backendMapping,
        country_code: countryCode,
      };

      const campaign = await calendarService.createCalendarCampaign(payload);
      setCreatedCampaign(campaign);
      setIsPendingApproval(isNewTemplateFlow);
      setStep(5);
    } catch (err: any) {
      console.error(err);
      const msg = err?.response?.data?.detail || err?.response?.data?.error_user_msg || err.message || "Error al crear la campaña";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="w-full space-y-6">

      {/* ── Step 0: Calendar selection (Mapped to 'Datos' step) ─────────────────────── */}
      {step === 0 && (
        <Card className="mt-6">
          <CardContent className="space-y-6 p-4 sm:p-6">

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex-none bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white flex items-center justify-center shadow-md">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900">Configurar Campaña Calendar</div>
                <p className="text-xs text-gray-400">Conecta tu cuenta de Google Calendar y elige el calendario</p>
              </div>
            </div>

            {/* ── Guía Paso a Paso (Igual que otras campañas) ── */}
            <div className="mt-6 relative rounded-xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm">
              <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
              <div className="flex items-start gap-3 sm:gap-5 pl-3 sm:pl-4">
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Prepara tu calendario de Google</h4>
                  <p className="text-xs text-gray-600 leading-relaxed mb-4">
                    Antes de continuar, asegúrate de que tus eventos tengan el número de teléfono en la descripción configurado correctamente.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowTutorialModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-sm font-semibold text-white shadow-[0_4px_20px_rgba(28,179,179,0.4)] hover:shadow-[0_6px_25px_rgba(28,179,179,0.5)] transition-all animate-[pulse_2s_ease-in-out_infinite] hover:animate-none"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    Ver guía paso a paso
                  </button>
                </div>
              </div>
            </div>

            {/* Calendario selection */}
            <Card className="mt-4 group relative rounded-xl border border-gray-200 bg-white p-4 mb-4 shadow-[0_8px_30px_rgba(12,20,30,0.06)]">
              <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
              <CardContent className="pl-4">
                <div className="mb-3">
                  <div className="text-lg font-semibold text-gray-900 mb-0">Selecciona el calendario</div>
                  <p className="text-xs text-gray-500 mt-1">Escoge de dónde extraeremos los eventos.</p>
                </div>

                {/* Grid de calendarios con paginado 4×4 */}
                {localCalendars.length === 0 ? (
                  <div className="flex items-center gap-2 p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm">
                    <Info className="w-4 h-4 flex-shrink-0 text-amber-500" />
                    No hay calendarios disponibles. Conecta una cuenta de Google Calendar.
                  </div>
                ) : (() => {
                  const totalCals = localCalendars.length;
                  const totalCalPages = Math.ceil(totalCals / CAL_PER_PAGE);
                  const pagedCals = localCalendars.slice(calPage * CAL_PER_PAGE, (calPage + 1) * CAL_PER_PAGE);
                  return (
                    <>
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        {pagedCals.map((cal) => {
                          const hasActiveCampaign = calendarHasActiveCampaign(cal.id);
                          const isSelected = selectedCalendarId === cal.id;
                          const isInactive = !cal.is_active;
                          const isActivating = activatingCal === cal.id;

                          const handleClick = () => {
                            if (hasActiveCampaign || isActivating) return;
                            if (isInactive) { activateAndSelect(cal.id); }
                            else { setSelectedCalendarId(cal.id); }
                          };

                          return (
                            <div key={cal.id} className="relative group/cal">
                              <div
                                onClick={handleClick}
                                className={`border-2 rounded-lg p-4 cursor-pointer transition-all relative min-h-[100px] flex flex-col justify-center
                                  ${isSelected
                                    ? 'border-[#1CB3B3] bg-gradient-to-r from-[#1CB3B3]/10 to-[#179999]/10 ring-1 ring-[#1CB3B3]/20'
                                    : hasActiveCampaign
                                      ? 'opacity-60 cursor-not-allowed border-gray-100 bg-gray-50'
                                      : isInactive
                                        ? 'border-dashed border-gray-300 hover:border-[#1CB3B3]/50 hover:bg-teal-50/20'
                                        : 'border-gray-200 bg-white hover:border-[#1CB3B3]/40 hover:shadow-md'}`}
                              >
                                {/* Botón absoluto como en Google Sheets */}
                               <div className="flex justify-end mb-3 xl:absolute xl:right-3 xl:top-3 xl:mb-0">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(`https://calendar.google.com/calendar/r/settings/calendar/${cal.id}`, '_blank', 'noopener,noreferrer');
                                    }}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-[#1CB3B3] text-[#0D7377] text-xs font-medium hover:bg-[#E8F7F7] transition-all"
                                    title="Abrir en Google Calendar"
                                  >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                    Abrir calendario
                                  </button>
                                </div>

                                {/* Content: Name and Date */}
                                <div className="min-w-0 xl:pr-24">
                                  <h4 className={`font-medium mb-1 break-words xl:truncate ${isInactive ? 'text-gray-400' : 'text-gray-900'}`}>
                                      {cal.summary}
                                  </h4>
                                  <div className="flex flex-col gap-1.5 mt-2">
                                    <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                                      <Clock className="w-3 h-3" />
                                      Modificado: {cal.updated
                                        ? new Date(cal.updated).toLocaleDateString()
                                        : 'Reciente'}
                                    </div>

                                    <div className="flex items-center gap-2 mt-1">
                                      {isSelected && !hasActiveCampaign && (
                                        <div className="inline-flex items-center gap-1.5 bg-[#1CB3B3] text-white text-[10px] px-2.5 py-1 rounded-md font-bold shadow-sm">
                                          <Check className="h-3.5 w-3.5" /> Seleccionada
                                        </div>
                                      )}
                                      {hasActiveCampaign && (
                                        <div className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-[9px] px-2 py-0.5 rounded-md font-semibold border border-amber-200">
                                          Campaña en uso
                                        </div>
                                      )}
                                      {isInactive && !hasActiveCampaign && (
                                        <span className="text-[10px] text-gray-400 italic">Clic para conectar</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {hasActiveCampaign && (
                                <div className="absolute left-0 bottom-full mb-1.5 w-64 p-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover/cal:opacity-100 transition-opacity pointer-events-none z-10 whitespace-normal leading-relaxed shadow-xl">
                                  Ya existe una campaña en uso (activa o pausada). Cancélala primero.
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Paginado del grid */}
                      {totalCalPages > 1 && (
                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                          <button
                            onClick={() => setCalPage(p => Math.max(0, p - 1))}
                            disabled={calPage === 0}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                          >
                            <ArrowLeft className="w-3 h-3" /> Anterior
                          </button>
                          <span className="text-xs text-gray-400">{calPage + 1} / {totalCalPages} &middot; {totalCals} calendarios</span>
                          <button
                            onClick={() => setCalPage(p => Math.min(totalCalPages - 1, p + 1))}
                            disabled={calPage >= totalCalPages - 1}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                          >
                            Siguiente <ArrowRight className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Acciones: crear nuevo calendario / cambiar cuenta */}
                <div className="mt-5 pt-4 border-t border-gray-100">

                  {/* Formulario inline para crear un nuevo calendario */}
                  {showCreateCalForm ? (
                    <div className="p-4 rounded-xl border border-[#1CB3B3]/30 bg-[#F0FAFA] space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-700">Nuevo calendario de Google</span>
                        <button
                          onClick={() => { setShowCreateCalForm(false); setNewCalName(''); setNewCalDesc(''); }}
                          className="text-[10px] text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                        >
                          Cancelar ✕
                        </button>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Nombre del calendario *</label>
                        <input
                          autoFocus
                          type="text"
                          value={newCalName}
                          onChange={e => setNewCalName(e.target.value)}
                          placeholder="Ej: Citas Clientes 2024"
                          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-[#1CB3B3] focus:ring-1 focus:ring-[#1CB3B3]/30 bg-white"
                          onKeyDown={e => e.key === 'Enter' && createNewCalendar()}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Descripción (opcional)</label>
                        <input
                          type="text"
                          value={newCalDesc}
                          onChange={e => setNewCalDesc(e.target.value)}
                          placeholder="Para qué se usa este calendario"
                          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-[#1CB3B3] focus:ring-1 focus:ring-[#1CB3B3]/30 bg-white"
                        />
                      </div>
                      <Button
                        onClick={createNewCalendar}
                        disabled={creatingCal || !newCalName.trim()}
                        className="w-full bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white h-10 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg active:scale-95 disabled:opacity-50 transition-all"
                      >
                        {creatingCal ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Creando...</>
                        ) : (
                          <><Plus className="w-4 h-4" /> Crear calendario</>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                      {localCalendars.length === 0 ? (
                        /* Botón para conectar por primera vez */
                        <Button
                          onClick={async () => {
                            await startCalendarCampaignOAuth(
                              CALENDAR_CAMPAIGN_CONTEXT,
                              initialCampaignName
                            );
                          }}
                          className="w-auto max-w-full px-4 sm:px-6 bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white min-h-11 h-auto py-2.5 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg active:scale-95 transition-all whitespace-normal text-center leading-tight"
                        >
                         <Plus className="w-4 h-4 shrink-0" />
                          Conectar Google Calendar
                        </Button>
                      ) : (
                        <>
                          {/* Botón 1: crear nuevo calendario — estilo primario Reveca */}
                          <Button
                            onClick={() => setShowCreateCalForm(true)}
                            className="w-full max-w-full sm:flex-1 sm:max-w-xs bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white min-h-11 h-auto py-2.5 px-3 sm:px-4 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-md active:scale-95 transition-all text-sm whitespace-normal text-center leading-tight"
                          >
                            <Plus className="w-4 h-4 shrink-0" />
                            Crear nuevo calendario
                          </Button>

                          {/* Botón 2: cambiar cuenta Google — estilo outline */}
                          <Button
                            variant="outline"
                            disabled={changingAccount}
                            onClick={async () => {
                              if (!token) {
                                toast.error('Inicia sesión para cambiar la cuenta');
                                return;
                              }
                              setChangingAccount(true);
                              try {
                                setSelectedCalendarId(null);
                                setLocalCalendars([]);
                                await changeCalendarAccount(
                                  CALENDAR_CAMPAIGN_CONTEXT,
                                  initialCampaignName
                                );
                              } catch {
                                toast.error('Error al cambiar la cuenta de Google Calendar');
                                void reloadLocalCalendars();
                              } finally {
                                setChangingAccount(false);
                              }
                            }}
                            className="w-full max-w-full sm:flex-1 sm:max-w-xs border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400 min-h-11 h-auto py-2.5 px-3 sm:px-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all text-sm whitespace-normal text-center leading-tight disabled:opacity-60"
                          >
                            {changingAccount ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <ExternalLink className="w-4 h-4 shrink-0" />
                            )}
                            Cambiar cuenta de Google Calendar
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Collapsible format instructions */}
                <div className="w-full min-w-0 bg-cyan-50 border border-cyan-200 rounded-xl mt-6 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setGuideOpen(!guideOpen)}
                    className="w-full min-w-0 flex items-start justify-between gap-2 p-3 sm:p-4 text-left hover:bg-cyan-100/40 transition-colors"
                  >
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      <AlertTriangle className="w-4 h-4 text-cyan-600 flex-shrink-0" />
                      <span className="min-w-0 text-xs sm:text-sm font-semibold text-cyan-900 leading-snug break-words">
                        Cómo configurar los eventos en Google Calendar
                      </span>
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 shrink-0 mt-0.5 text-cyan-600 transition-transform ${guideOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {guideOpen && (
                    <div className="px-4 pb-4 space-y-3 border-t border-cyan-200">
                      <ul className="text-xs text-cyan-800 list-disc list-inside space-y-1 mt-3">
                        <li>La <strong>fecha y hora</strong> del evento determina cuándo se enviará el recordatorio.</li>
                        <li>
                          Incluye el número de WhatsApp <strong>con código de país</strong> en la descripción del evento:
                          <code className="ml-1 bg-cyan-100/50 px-1 rounded font-mono">{countryCode} 3101234567</code>
                        </li>
                        <li>Si hay varios números en la descripción, se usará el primero.</li>
                        <li>Los eventos sin número de teléfono serán ignorados automáticamente.</li>
                      </ul>
                      <div className="border-t border-cyan-200 pt-3">
                        <p className="text-sm font-medium text-cyan-900 mb-2">Variables en la descripción (opcional)</p>
                        <p className="text-xs text-cyan-800 mb-2">
                          Si tu plantilla de WhatsApp usa variables, agrégalas en la descripción con el formato <code className="bg-cyan-100/50 px-1 rounded font-mono">nombre: valor</code>:
                        </p>
                        <div className="rounded-lg bg-white border border-cyan-100 font-mono text-xs p-3 space-y-0.5 leading-5 text-gray-800 shadow-sm">
                          <div className="text-gray-400"># Descripción del evento</div>
                          <div>{countryCode} 3101234567</div>
                          <div><span className="text-cyan-700">nombre</span>: Juan García</div>
                          <div><span className="text-cyan-700">servicio</span>: Internet</div>
                          <div><span className="text-cyan-700">valor</span>: $80,000</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              </CardContent>
            </Card>

            {/* ── Configuración de recordatorios ── */}
            <div className="mt-6">
              <AppointmentRemindersConfig
                useDefault={useDefault}
                setUseDefault={setUseDefault}
                reminderDays={reminderDays}
                setReminderDays={setReminderDays}
                reminderHours={reminderHours}
                setReminderHours={setReminderHours}
              />
            </div>

          </CardContent>
          <CardFooter className="pt-6 border-t border-gray-100">
            <div className="w-full flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <Button
                variant="outline"
                onClick={() => onCancel()}
                className="w-full sm:w-auto border-gray-300 text-gray-600 hover:bg-gray-50 px-4 sm:px-6 h-11 rounded-xl shadow-sm"
              >
                ← Cancelar
              </Button>
              <Button
                onClick={nextStep}
                disabled={!canNext()}
                className="w-full sm:w-auto bg-gradient-to-r from-[#1CB3B3] to-[#179999] hover:from-[#179999] hover:to-[#1CB3B3] text-white font-semibold px-4 sm:px-8 h-11 rounded-xl shadow-lg hover:shadow-cyan-500/30 transform transition-all active:scale-95 disabled:opacity-50"
              >
                Siguiente →
              </Button>
            </div>
          </CardFooter>
        </Card>
      )}

      {/* Step 1 skipped — reminders now configured in step 0 */}

      {/* ── Step 2: Template selection (shared component) ──────────────────── */}
      {step === 2 && (
        <StepTemplate
          templateId={selectedTemplateId}
          setTemplateId={setSelectedTemplateId}
          templates={templates}
          showTemplateModal={showTemplateModal}
          setShowTemplateModal={setShowTemplateModal}
          handleTemplateCreated={(id) => {
            if (id) setSelectedTemplateId(id);
            setShowTemplateModal(false);
            reloadProcessedTemplates().catch(() => { });
          }}
          templateType={templateType}
          setTemplateType={setTemplateType}
          templateLanguage={templateLanguage}
          setTemplateLanguage={setTemplateLanguage}
          templateNameInput={templateNameInput}
          setTemplateNameInput={setTemplateNameInput}
          setMessageTemplate={setMessageTemplate}
          campaignName={campaignName}
          isLoadingTemplates={loadingTemplates}
          onBack={prevStep}
          onNext={() => setStep(3)}
          campaignType="Calendar"
        />
      )}

      {/* ── Step 3: Mensaje y mapeo de variables ────────────────────────────── */}
      {step === 3 && (
        <StepTemplateMessage
          messageTemplate={messageTemplate}
          setMessageTemplate={setMessageTemplate}
          sheetColumns={mappingOptionLabels}
          sampleRow={sampleRowMock}
          templateId={selectedTemplateId}
          templates={templates}
          columnMapping={columnMapping}
          setColumnMapping={setColumnMapping}

          headerText={templateHeaderText}
          setHeaderText={setTemplateHeaderText}
          footerText={templateFooterText}
          setFooterText={setTemplateFooterText}
          buttons={templateButtons}
          setButtons={setTemplateButtons}

          parentHeaderType={templateHeaderType}
          setParentHeaderType={setTemplateHeaderType}
          parentHeaderImageHandle={templateHeaderImageHandle}
          setParentHeaderImageHandle={setTemplateHeaderImageHandle}
          setHeaderImageUrl={setTemplateHeaderImageUrl}
          parentHeaderImagePreview={templateHeaderImagePreview}
          setParentHeaderImagePreview={setTemplateHeaderImagePreview}

          parentHeaderEnabled={templateHeaderEnabled}
          setParentHeaderEnabled={setTemplateHeaderEnabled}
          parentFooterEnabled={templateFooterEnabled}
          setParentFooterEnabled={setTemplateFooterEnabled}
          parentButtonsEnabled={templateButtonsEnabled}
          setParentButtonsEnabled={setTemplateButtonsEnabled}

          onBack={prevStep}
          onNext={nextStep}
          campaignType="Calendar"
          reloadColumns={async () => {
            if (selectedCalendarId) {
              setLoadingEvents(true);
              try {
                const evts = await calendarService.getEvents(selectedCalendarId);
                setEvents(evts || []);
              } catch (e) {
                console.error(e);
              } finally {
                setLoadingEvents(false);
              }
            }
          }}
        />
      )}

      {/* ── Step 4: Resumen Narrativo (Estructura de Cuadro Padre) ────────────────── */}
      {step === 4 && (
        <Card className="mt-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
          <CardContent className="space-y-6 p-4 sm:p-6">

            {/* Header consistente con Step 0 */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex-none bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white flex items-center justify-center shadow-md">
                <Check className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-lg font-semibold text-gray-900 leading-tight break-words">Confirmación de Campaña</div>
                <p className="text-xs text-gray-400">Verifica los detalles finales antes de activar el servicio.</p>
              </div>
            </div>

            {/* Cuadro de resumen (como el cuadro de "Prepara tú hoja") */}
            <div className="relative rounded-xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm overflow-hidden min-h-[300px]">
              <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 xl:gap-8 relative z-10">
                {/* Lado Izquierdo: Configuración e Hilo Narrativo */}
                <div className="space-y-6">
                  {/* Origen */}
                  <div className="flex gap-3 sm:gap-4 items-start">
                    <div className="w-9 h-9 rounded-lg bg-teal-50 text-[#1CB3B3] flex items-center justify-center flex-shrink-0 border border-teal-100/50">
                      <Calendar className="w-4.5 h-4.5" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="text-[10px] font-bold text-[#1F4068]/60 uppercase tracking-widest">Configuración Campaña</p>
                      <p className="text-sm text-gray-700 leading-snug break-words [overflow-wrap:anywhere]">
                        Se activará <span className="font-bold text-gray-900">"{campaignName}"</span> sobre el calendario
                        <span className="font-bold text-[#1F4068] mx-1 italic">
                          {localCalendars.find(c => c.id === selectedCalendarId)?.summary || 'Principal'}
                        </span>.
                      </p>
                    </div>
                  </div>

                  {/* Mensaje */}
                  <div className="flex gap-3 sm:gap-4 items-start border-t border-slate-100 pt-6">
                    <div className="w-9 h-9 rounded-lg bg-teal-50 text-[#1CB3B3] flex items-center justify-center flex-shrink-0 border border-teal-100/50">
                      <MessageSquare className="w-4.5 h-4.5" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="text-[10px] font-bold text-[#1F4068]/60 uppercase tracking-widest">Plantilla y Variables</p>
                      <p className="text-sm text-gray-700 leading-snug break-words [overflow-wrap:anywhere]">
                        Uso de plantilla <span className="font-bold text-gray-900 font-mono text-[13px]">{selectedTemplateName || templateNameInput || "Personalizada"}</span> con
                        <span className="font-bold text-[#1C6E71] mx-1">{Object.keys(columnMapping).length} variables</span>{""} activas.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 sm:gap-4 items-start border-t border-slate-100 pt-6">
                    <div className="w-9 h-9 rounded-lg bg-teal-50 text-[#1CB3B3] flex items-center justify-center flex-shrink-0 border border-teal-100/50">
                      <Clock className="w-4.5 h-4.5" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="text-[10px] font-bold text-[#1F4068]/60 uppercase tracking-widest">Reglas de Envío</p>
                      <p className="text-sm text-gray-700 leading-snug break-words [overflow-wrap:anywhere]">
                        Programado para enviar <span className="font-bold text-[#1F4068]">
                          {useDefault
                            ? "1 día y 1 hora"
                            : (() => {
                              const components = [
                                ...reminderDays.map(d => `${d} ${d === 1 ? 'día' : 'días'}`),
                                ...reminderHours.map(h => `${h} ${h === 1 ? 'hora' : 'horas'}`)
                              ];
                              if (components.length === 0) return "al momento";
                              if (components.length === 1) return `${components[0]}`;
                              const last = components.pop();
                              return `${components.join(', ')} y ${last}`;
                            })()
                          } antes
                        </span> de cada evento.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col h-full min-w-0 bg-slate-50 shadow-inner rounded-2xl p-4 sm:p-5 border border-slate-100">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Contenido del Mensaje</span>
                    <span className="text-[9px] font-bold text-[#1CB3B3] bg-teal-50 border border-teal-100/50 px-2 py-0.5 rounded-full">Vista Previa</span>
                  </div>
                  <div className="flex-1 min-w-0 text-[13px] text-gray-600 leading-relaxed font-medium italic overflow-y-auto max-h-[160px] pr-2 break-words [overflow-wrap:anywhere] custom-scrollbar">
                    {messageTemplate || "Sin contenido definido."}
                  </div>

                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation buttons — hidden on step 2 and 3 (Steps have their own CardFooter) */}
      {/* ── Step 5: Éxito / Finalizado ─────────────────────────────── */}
      {step === 5 && (
        <div className="animate-in fade-in zoom-in duration-500 max-w-2xl mx-auto py-12 flex flex-col items-center justify-center min-h-[50vh] text-center">
          <div className="relative mb-8 inline-block">
            <div className="absolute inset-0 bg-emerald-200 rounded-full blur-2xl opacity-30 animate-pulse" />
            <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500 to-teal-400 text-white flex items-center justify-center shadow-xl shadow-emerald-500/20 mx-auto">
              <Check className="w-12 h-12" />
            </div>
          </div>

          <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight">¡Campaña Programada!</h2>

          <div className="bg-slate-50/80 border border-slate-100 rounded-3xl p-8 mb-8 text-left space-y-4 w-full">
            <p className="text-slate-600 leading-relaxed">
              La campaña <span className="font-bold text-slate-900">"{campaignName}"</span> se ha configurado correctamente.
            </p>

            {isPendingApproval ? (
              <div className="flex gap-4 p-5 rounded-2xl bg-amber-50 border border-amber-100 items-start">
                <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 shadow-sm" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-bold text-amber-900 leading-tight">Pendiente de Aprobación</p>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    Hemos creado una nueva plantilla en Meta. Los recordatorios comenzarán a enviarse automáticamente una vez que WhatsApp apruebe el contenido (esto suele tardar máximo 24 horas).
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex gap-4 p-5 rounded-2xl bg-[#F0F9F9] border border-[#1CB3B3]/20 items-start">
                <div className="w-10 h-10 rounded-xl bg-[#1CB3B3] text-white flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-bold text-teal-900 leading-tight">Activación Inmediata</p>
                  <p className="text-xs text-teal-700 leading-relaxed">
                    Tu campaña está activa. El sistema monitoreará tu calendario constantemente y enviará los mensajes según las reglas que definiste.
                  </p>
                </div>
              </div>
            )}
          </div>

          <Button
            onClick={() => onCreated(createdCampaign)}
            className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:opacity-90 text-white px-10 h-14 rounded-2xl shadow-xl transition-all font-bold text-lg active:scale-95"
          >
            Ir a mis campañas
          </Button>
        </div>
      )}

      {/* Navigation Buttons (only for steps 0-4) */}
      {step < 5 && (
        <div className={`w-full flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mt-6 pt-4 border-t border-gray-100 ${(step === 0 || step === 2 || step === 3) ? 'hidden' : ''}`}>
          <Button
            variant="outline"
            onClick={() => {
              if (step === 0) { onCancel(); return; }
              prevStep();
            }}
            className="w-full sm:w-auto border-gray-300 text-gray-600 hover:bg-gray-50 px-4 sm:px-6 h-11 rounded-xl shadow-sm"
          >
            ← {step === 0 ? "Cancelar" : "Atrás"}
          </Button>

          {step < 4 ? (
            <Button
              onClick={nextStep}
              disabled={!canNext()}
              className="bg-slate-900 text-white hover:bg-slate-800 px-8 h-11 rounded-xl shadow-md font-bold transition-all active:scale-95 flex items-center gap-2"
            >
              Continuar
              <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full sm:w-auto bg-gradient-to-r from-[#1CB3B3] to-[#179999] hover:opacity-90 text-white px-4 sm:px-10 h-11 rounded-xl shadow-lg shadow-teal-500/20 font-bold transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Activando...
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5" />
                  Crear campaña
                </>
              )}
            </Button>
          )}
        </div>
      )}

      {/* Template creator modal */}
      <TemplateCreatorModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onCreated={() => {
          setShowTemplateModal(false);
          reloadProcessedTemplates().catch(() => { });
        }}
      />

      {/* Tutorial Modal */}
      {showTutorialModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => setShowTutorialModal(false)}>
          <div className="relative bg-white rounded-xl shadow-2xl max-w-[95vw] w-full max-h-[95vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowTutorialModal(false)} aria-label="Cerrar guía" className="absolute top-3 right-3 z-20 bg-white/80 hover:bg-white rounded-full p-1 focus:outline-none">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div className="flex items-center justify-center" style={{ padding: 12 }}>
              <img
                src="/assets/marketing.png"
                alt="Guía paso a paso - Google Calendar"
                className="rounded-md shadow-md"
                style={{ width: '100%', height: 'auto', maxHeight: 'calc(95vh - 24px)', objectFit: 'contain' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarCampaignBuilder;
